import { AddCircle, ChatBubble, EmojiSymbols, Pageview } from '@mui/icons-material';
import { Box, Button, Divider, Typography } from '@mui/material';
import React, { useContext, useState } from 'react';
import { AppContext } from '../../AppContext';
import { Actions } from '../../enums/ActionEnums';
import { Categories } from '../../enums/RoleEnums';
import {
  GetInterestGroupsDocument,
  IGetInterestGroupsQuery,
  IInterest,
  IInterestGroup,
  useDeleteInterestMutation,
  useGetInterestGroupsQuery
} from '../../generated/graphql';
import { Colors } from '../../utils/theme';
import OverviewToolbar from '../Common/Layout/OverviewToolbar';
import OverviewTable from '../Common/Tables/OverviewTable';
import { colorings, makeFirstLetterCapital } from './Users/UsersList';
import AddInterestsToCategory from './AddCategoryInterests';
import EpraChip from '../Common/Fields/EpraChip';

const iconAndColor = (categoryFromList: string) => {
  switch (categoryFromList) {
    case Categories.Comminications:
      return <ChatBubble style={{ color: colorings(categoryFromList), marginRight: 10 }} />;
    case Categories.Research:
      return <Pageview style={{ color: colorings(categoryFromList), marginRight: 10 }} />;
  }
};

export type InterestType = IInterestGroup;

const Interests: React.FunctionComponent = () => {
  const { data, loading } = useGetInterestGroupsQuery();
  const [deleteInterest] = useDeleteInterestMutation();
  const { dispatch } = useContext(AppContext);
  const [openAddInterestForm, setOpenAddInterestForm] = useState(false);
  const [selectedInterestList, setSelectedInterestList] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState('');

  const renderCategories = ({ name }: IInterestGroup) => {
    return (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(12, minmax(0, 1fr))'
        }}
      >
        <Box sx={{ gridColumn: 'span 1', height: '50px' }}>
          <Divider orientation="vertical" sx={{ background: colorings(name), width: '3px' }} />
        </Box>
        <Box sx={{ gridColumn: 'span 11', marginTop: '10px' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center'
            }}
          >
            {iconAndColor(name)}
            <Typography
              id={`${name}-category`}
              data-testid={`${name}-category`}
              sx={{
                fontFamily: 'Overpass',
                fontSize: '14px',
                color: colorings(name),
                marginTop: '5px',
                marginRight: '25px'
              }}
            >
              {makeFirstLetterCapital(name)}
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  };

  const renderInterests = ({ id, name, interests }: IInterestGroup) => {
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
        {interests.map((interest, index) => (
          <Box key={index} sx={{ margin: '10px' }}>
            <EpraChip
              id={`${interest?.name}-interest`}
              data-testid={`${interest?.name}-interest`}
              label={interest?.name ?? ''}
              onDelete={() => handleRemoveInterest(interest as IInterest, id, name)}
            />
          </Box>
        ))}
      </Box>
    );
  };

  const handleRemoveInterest = (interest: IInterest, groupId: string, groupName: string) => {
    dispatch({
      type: Actions.ShowConfirmation,
      payload: {
        text: `Are you sure you want to remove ${interest.name} from ${groupName} group?`,
        agreeAction: removeInterestConfirmed,
        params: [interest, groupId, groupName]
      }
    });
  };

  const removeInterestConfirmed = (params: string[]) => {
    const interest = params[0] as unknown as IInterest;
    const interestId = interest.id;
    const groupId = params[1];
    const groupName = params[2];
    dispatch({ type: Actions.HideConfirmation });
    deleteInterest({
      variables: {
        id: parseInt(interestId)
      },
      update(cache) {
        const interestGroups = cache.readQuery({
          query: GetInterestGroupsDocument
        }) as IGetInterestGroupsQuery;
        if (interestGroups.result) {
          const data = interestGroups.result.map((gr) => {
            if (gr?.id === groupId) {
              return { ...gr, interests: gr.interests.filter((i) => i?.id !== interestId) };
            } else {
              return gr;
            }
          });
          cache.writeQuery({
            query: GetInterestGroupsDocument,
            data: { result: data }
          });
        }

        dispatch({
          type: Actions.ShowMessage,
          payload: {
            text: `Interest ${interest.name} removed from ${groupName} group`,
            severity: 'success'
          }
        });
      }
    }).catch((error) =>
      dispatch({
        type: Actions.ShowMessage,
        payload: {
          severity: 'error',
          text: error.message,
          autoHide: 6000
        }
      })
    );
  };

  const renderAddInterest = ({ id, name }: IInterestGroup) => {
    return (
      <Button
        id={`${id}-${name}-add-interest-button`}
        data-testid={`${id}=${name}-add-interest-button`}
        onClick={() => handleOpenAddInterestToCategory(id, name)}
      >
        <AddCircle style={{ color: Colors.skyBlue, height: 42, width: 42 }} />
      </Button>
    );
  };

  const handleOpenAddInterestToCategory = (selectedCategoryId: string, name: string) => {
    setOpenAddInterestForm(true);
    setSelectedCategoryId(selectedCategoryId);
    setSelectedInterestList(name.toUpperCase());
  };

  const handleCloseAddInterestToCategory = () => {
    setOpenAddInterestForm(false);
    setSelectedCategoryId('');
    setSelectedInterestList('');
  };

  const columns = [
    { title: 'Interest Category', width: 457, sorting: false, render: renderCategories },
    { title: 'Interests', width: 1314, sorting: false, render: renderInterests },
    { title: 'Add Interest', width: 140, sorting: false, render: renderAddInterest }
  ];
  return (
    <Box className="overviewPage">
      <Box sx={{ width: '100%', zIndex: 2 }}>
        <OverviewToolbar>
          <EmojiSymbols style={{ marginRight: '7px' }} /> Interests
        </OverviewToolbar>
      </Box>
      <Box>
        <OverviewTable
          columns={columns}
          data={data?.result || []}
          loading={loading}
          handleDetails={() => true}
          handleRemove={() => true}
          pageSize={10}
          hideSearch={true}
        />
      </Box>
      {openAddInterestForm && (
        <AddInterestsToCategory
          name={selectedInterestList}
          interests={
            data?.result.find((ig) => ig?.name.toUpperCase() === selectedInterestList.toUpperCase())
              ?.interests as IInterest[]
          }
          id={selectedCategoryId}
          handleClose={handleCloseAddInterestToCategory}
        />
      )}
    </Box>
  );
};

export default Interests;
